import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, Trash2, UserPlus } from "lucide-react";
import { api, errMsg, useFetch, fmtDate } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { DIVISIONS } from "@/lib/content";
import { FieldInput } from "@/components/admin/FieldInput";
import { SETTINGS_GROUPS } from "@/components/admin/schemas";

export const SettingsPanel = () => {
  const { settings, reload } = useSite();
  const [form, setForm] = useState(settings);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setForm(settings);
  }, [settings]);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", form);
      await reload();
      toast.success("Website settings updated");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-8" data-testid="settings-panel">
      {SETTINGS_GROUPS.map(([title, fields]) => (
        <div key={title} className="card-navy p-6">
          <p className="font-display text-2xl font-black uppercase italic mb-5">{title}</p>
          <div className="grid gap-5 md:grid-cols-2">
            {fields.map((f) => (
              <div key={f.k} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                <label className="label">{f.label}</label>
                <FieldInput field={f} value={form[f.k]} onChange={(v) => setForm((p) => ({ ...p, [f.k]: v }))} prefix="settings" />
              </div>
            ))}
          </div>
        </div>
      ))}
      <button onClick={save} disabled={busy} className="btn-gold" data-testid="settings-save-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Save Website Settings</button>
    </div>
  );
};

export const InquiriesPanel = () => {
  const { data, loading, reload } = useFetch("/admin/inquiries");
  const setStatus = async (id, status) => {
    await api.put(`/admin/inquiries/${id}`, { status });
    reload();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    await api.delete(`/admin/inquiries/${id}`);
    reload();
  };
  if (loading) return <p className="text-slate-400">Loading…</p>;
  if (!data?.length) return <p className="text-slate-400" data-testid="inquiries-empty">No inquiries yet.</p>;
  return (
    <div className="space-y-3" data-testid="inquiries-panel">
      {data.map((q) => (
        <div key={q.id} className="card-navy p-5" data-testid={`inquiry-${q.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-gold">{q.type} • {q.category} • {fmtDate(q.created_at)}</p>
              <p className="font-display text-xl font-extrabold uppercase">{q.name}{q.organization ? ` — ${q.organization}` : ""}</p>
              <p className="text-xs text-slate-400">{q.email} {q.phone && `• ${q.phone}`}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-[10px] font-mono uppercase px-2 py-1 border ${q.email_status === "sent" ? "border-emerald-500 text-emerald-400" : "border-red-500 text-red-400"}`}>Email {q.email_status === "sent" ? "sent" : "not sent"}</span>
              <select value={q.status} onChange={(e) => setStatus(q.id, e.target.value)} className="field !py-1.5 !w-auto" data-testid={`inquiry-status-${q.id}`}>
                {["new", "read", "resolved"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <a href={`mailto:${q.email}`} className="p-2 text-slate-300 hover:text-gold" aria-label="Reply"><Mail className="h-4 w-4" /></a>
              <button onClick={() => remove(q.id)} className="p-2 text-slate-300 hover:text-red-400" aria-label="Delete" data-testid={`inquiry-delete-${q.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
          {q.interests?.length > 0 && <p className="mt-2 text-xs text-slate-400">Interests: {q.interests.join(", ")}</p>}
          <p className="mt-3 text-sm text-slate-200 whitespace-pre-line">{q.message}</p>
        </div>
      ))}
    </div>
  );
};

const NewUser = ({ onDone }) => {
  const [f, setF] = useState({ name: "", email: "", password: "", role: "coach", team_slug: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/users", f);
      toast.success("Account created");
      setF({ name: "", email: "", password: "", role: "coach", team_slug: "" });
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <form onSubmit={submit} className="card-navy p-5 grid gap-3 md:grid-cols-6 items-end" data-testid="new-user-form">
      <input required placeholder="Name" className="field md:col-span-1" value={f.name} onChange={set("name")} data-testid="new-user-name" />
      <input required type="email" placeholder="Email" className="field md:col-span-2" value={f.email} onChange={set("email")} data-testid="new-user-email" />
      <input required minLength={8} type="password" placeholder="Temp password" className="field" value={f.password} onChange={set("password")} data-testid="new-user-password" />
      <select className="field" value={f.role} onChange={set("role")} data-testid="new-user-role">{["coach", "parent", "player", "admin"].map((r) => <option key={r} value={r}>{r}</option>)}</select>
      <select className="field" value={f.team_slug} onChange={set("team_slug")} data-testid="new-user-team"><option value="">No team</option>{DIVISIONS.map((d) => <option key={d.slug} value={d.slug}>{d.name}</option>)}</select>
      <button className="btn-gold !py-2.5 !text-sm md:col-span-6 md:justify-self-start" data-testid="new-user-submit"><UserPlus className="h-4 w-4" /> Create Account</button>
    </form>
  );
};

export const UsersPanel = ({ me }) => {
  const { data, reload } = useFetch("/admin/users");
  const [filter, setFilter] = useState("all");
  const all = data || [];
  const count = (k) => (k === "all" ? all.length : k === "pending" ? all.filter((u) => !u.approved).length : all.filter((u) => u.role === k).length);
  const users = all
    .filter((u) => filter === "all" || (filter === "pending" ? !u.approved : u.role === filter))
    .sort((a, b) => Number(!!a.approved) - Number(!!b.approved));
  const update = async (id, patch) => {
    try {
      await api.put(`/admin/users/${id}`, patch);
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const remove = async (u) => {
    if (!window.confirm(`Delete account for ${u.email}?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div className="space-y-5" data-testid="users-panel">
      <p className="text-sm text-slate-400">Everyone gets their own login. New sign-ups (parents, players, coaches and admins) stay pending until you approve them here. Coach KP is emailed whenever someone signs up. Coaches only manage their assigned division.</p>
      <NewUser onDone={reload} />
      <div className="flex flex-wrap gap-2" data-testid="users-filter">
        {["all", "pending", "admin", "coach", "parent", "player"].map((k) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`users-filter-${k}`}
            className={`px-3 py-2 text-xs font-bold uppercase tracking-wider border transition-colors ${filter === k ? "bg-gold text-navy border-gold" : "border-line text-slate-300 hover:border-gold"}`}>
            {k} ({count(k)})
          </button>
        ))}
      </div>
      <div className="card-navy overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead><tr className="border-b border-line text-left font-mono text-[10px] uppercase tracking-[0.2em] text-gold"><th className="p-3">User</th><th className="p-3">Role</th><th className="p-3">Team</th><th className="p-3">Approved</th><th className="p-3">Registration</th><th className="p-3">Payment</th><th className="p-3"></th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-line/50" data-testid={`user-row-${u.id}`}>
                <td className="p-3"><p className="font-semibold">{u.name} {!u.approved && <span className="ml-1 bg-gold px-1.5 py-0.5 font-mono text-[9px] uppercase text-navy" data-testid={`user-pending-${u.id}`}>Pending</span>}</p><p className="text-xs text-slate-400">{u.email}{u.phone ? ` • ${u.phone}` : ""}</p>{u.requested_role && <p className="text-xs text-slate-500">Signed up as: {u.requested_role}</p>}{u.athletes && <p className="text-xs text-slate-500">Athletes: {u.athletes}</p>}</td>
                <td className="p-3"><select className="field !py-1.5" value={u.role} disabled={u.id === me.id} onChange={(e) => update(u.id, { role: e.target.value })} data-testid={`user-role-${u.id}`}>{["admin", "coach", "parent", "player"].map((r) => <option key={r} value={r}>{r}</option>)}</select></td>
                <td className="p-3"><select className="field !py-1.5" value={u.team_slug || ""} onChange={(e) => update(u.id, { team_slug: e.target.value })} data-testid={`user-team-${u.id}`}><option value="">—</option>{DIVISIONS.map((d) => <option key={d.slug} value={d.slug}>{d.name}</option>)}</select></td>
                <td className="p-3"><input type="checkbox" className="h-5 w-5 accent-[#FFCB05]" checked={!!u.approved} disabled={u.id === me.id} onChange={(e) => update(u.id, { approved: e.target.checked })} data-testid={`user-approved-${u.id}`} /></td>
                <td className="p-3"><input className="field !py-1.5" defaultValue={u.registration_status} onBlur={(e) => e.target.value !== u.registration_status && update(u.id, { registration_status: e.target.value })} data-testid={`user-registration-${u.id}`} /></td>
                <td className="p-3"><input className="field !py-1.5" defaultValue={u.payment_status} onBlur={(e) => e.target.value !== u.payment_status && update(u.id, { payment_status: e.target.value })} data-testid={`user-payment-${u.id}`} /></td>
                <td className="p-3 text-right">{u.id !== me.id && <button onClick={() => remove(u)} className="p-2 text-slate-300 hover:text-red-400" aria-label="Delete user" data-testid={`user-delete-${u.id}`}><Trash2 className="h-4 w-4" /></button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
